/**
 * AI assistant permission presets (ChatGPT Custom GPT keys + MCP connector
 * grants). The owner picks a preset — or ticks a custom subset — and the
 * assistant gets exactly those registry codes, intersected with the owner's
 * own permissions (and re-intersected on every request server-side).
 *
 * Security/admin permissions are NEVER assistant-grantable, whatever the
 * preset: users.*, roles.*, audit.*, admin.*, integrations.manage,
 * business.settings and the client-portal codes.
 */

import { NORD_LIVING_PERMISSIONS } from "./permission-registry";

export const ASSISTANT_PRESETS = [
  "read_only",
  "drafts",
  "record_money",
  "full",
  "custom",
] as const;

export type AssistantPreset = (typeof ASSISTANT_PRESETS)[number];

export const DEFAULT_ASSISTANT_PRESET: AssistantPreset = "drafts";

const FORBIDDEN_CATEGORIES: ReadonlySet<string> = new Set([
  "users",
  "roles",
  "audit",
  "admin",
  "portal",
]);

const FORBIDDEN_CODES: ReadonlySet<string> = new Set([
  "integrations.manage",
  "business.settings",
]);

/** Every code an assistant may ever hold — the `full` preset's list. */
export const ASSISTANT_GRANTABLE_PERMISSIONS: readonly string[] =
  NORD_LIVING_PERMISSIONS.filter(
    (p) =>
      !FORBIDDEN_CATEGORIES.has(p.category) && !FORBIDDEN_CODES.has(p.code),
  ).map((p) => p.code);

const GRANTABLE: ReadonlySet<string> = new Set(ASSISTANT_GRANTABLE_PERMISSIONS);

const READ_ONLY = ASSISTANT_GRANTABLE_PERMISSIONS.filter((c) =>
  c.endsWith(".view"),
);

const DRAFTS = [
  ...READ_ONLY,
  "clients.manage",
  "projects.manage",
  "proposals.manage",
  "invoices.manage",
  "purchases.manage",
  "expenses.manage",
  "approvals.manage",
];

const RECORD_MONEY = [
  ...DRAFTS,
  "payments.manage",
  "owner.manage",
  "banking.import",
  "banking.reconcile",
];

const PRESET_CODES: Readonly<
  Record<Exclude<AssistantPreset, "custom">, readonly string[]>
> = {
  read_only: READ_ONLY,
  drafts: DRAFTS,
  record_money: RECORD_MONEY,
  full: ASSISTANT_GRANTABLE_PERMISSIONS,
};

export function isAssistantPreset(value: unknown): value is AssistantPreset {
  return (
    typeof value === "string" &&
    (ASSISTANT_PRESETS as readonly string[]).includes(value)
  );
}

/** True when an assistant may ever hold this code (never security/admin). */
export function isAssistantGrantable(code: string): boolean {
  return GRANTABLE.has(code);
}

/** Drop anything outside the assistant ceiling (and the "*" wildcard). */
export function capToAssistantCeiling(codes: readonly string[]): string[] {
  return [...new Set(codes)].filter((c) => GRANTABLE.has(c));
}

/** The codes a preset stands for, before the owner intersection. */
export function presetPermissionCodes(
  preset: Exclude<AssistantPreset, "custom">,
): readonly string[] {
  return PRESET_CODES[preset];
}

export interface ResolvePresetInput {
  preset: AssistantPreset;
  /** The owner's own permission codes (an admin owner holds every code). */
  ownerPermissions: readonly string[];
  /** Ticked codes when preset is "custom". */
  custom?: readonly string[];
}

/**
 * The assistant's stored permission list: the preset (or the custom ticks)
 * within the ceiling, intersected with what the owner holds. Sorted, unique.
 */
export function resolveAssistantPermissions(
  input: ResolvePresetInput,
): string[] {
  const owner = new Set(input.ownerPermissions);
  const wanted =
    input.preset === "custom"
      ? (input.custom ?? [])
      : presetPermissionCodes(input.preset);
  return capToAssistantCeiling(wanted)
    .filter((c) => owner.has(c))
    .sort();
}

/** The preset whose resolved list equals `codes` for this owner, else custom. */
export function detectAssistantPreset(
  codes: readonly string[],
  ownerPermissions: readonly string[],
): AssistantPreset {
  const key = [...new Set(codes)].sort().join(",");
  const named = ASSISTANT_PRESETS.filter((p) => p !== "custom");
  const match = named.find(
    (preset) =>
      resolveAssistantPermissions({ preset, ownerPermissions }).join(",") ===
      key,
  );
  return match ?? "custom";
}
